import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { useError } from '../contexts/ErrorContext';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSIONS } from '../utils/permissions';

interface Staff {
  id: string;
  name: string;
  status: 'Hoạt động' | 'Đã nghỉ';
  role: string;
}

interface Schedule {
  id: string;
  staffId: string;
  workDate: string;
  shift: string;
  staffName: string;
  staffRole: string;
  staffColor: string;
  staffTextColor: string;
}

interface WeekInfo {
  startDate: string;
  endDate: string;
  weekNumber: number;
}

interface Holiday {
  id?: string;
  holiday_date: string;
  name: string;
  multiplier: number;
}

export interface RegistrationLockInfo {
  weekStartDate: string;
  weekEndDate: string;
  deadline: string;
  isPastDeadline: boolean;
  isUnlockedByAdmin: boolean;
  isLocked: boolean;
  canRegister: boolean;
  unlockedBy?: string | null;
  unlockedAt?: string | null;
  note?: string | null;
}

const SHIFTS = ['Sáng', 'Chiều', 'Tối'];
const DAYS = ['Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ Nhật'];

export const RegisterPage: React.FC = () => {
  const { setLoading } = useLoading();
  const { error, setError, clearError } = useError();
  const { user, can } = useAuth();
  const { startDate, endDate } = useParams<{ startDate?: string; endDate?: string }>();
  const navigate = useNavigate();
  
  // State management
  const [staff, setStaff] = useState<Staff[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [currentWeek, setCurrentWeek] = useState<WeekInfo>(() => {
    if (startDate && endDate) {
      return {
        startDate,
        endDate,
        weekNumber: getWeekNumberFromDate(startDate)
      };
    }
    return getCurrentWeek();
  });
  const [selectedStaffId, setSelectedStaffId] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{
    toAdd: Array<{ staffId: string; workDate: string; shift: string }>;
    toDelete: string[];
  }>({ toAdd: [], toDelete: [] });
  const [staffSearch, setStaffSearch] = useState('');
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);
  const checkIsMobile = () => window.innerWidth < 768 || (window.innerHeight <= 500 && window.innerWidth <= 950);
  const [isMobile, setIsMobile] = useState(checkIsMobile());
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [lockInfo, setLockInfo] = useState<RegistrationLockInfo | null>(null);
  const [isTogglingLock, setIsTogglingLock] = useState(false);
  const [isTriggeringReminder, setIsTriggeringReminder] = useState(false);

  // Quản lý lịch: xếp/xóa ca cho mọi người và đăng ký khi cổng đã khóa
  const isUserAdmin = can(PERMISSIONS.schedule);

  // Prevent duplicate API calls
  const isLoadingRef = useRef<boolean>(false);
  const staffSearchRef = useRef<HTMLDivElement>(null);

  // Check if mobile on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(checkIsMobile());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper functions
  function getWeekNumberFromDate(dateString: string): number {
    // Parse date string YYYY-MM-DD directly
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const startOfYear = new Date(year, 0, 1);
    const weekNumber = Math.ceil((date.getTime() - startOfYear.getTime()) / (7 * 24 * 60 * 60 * 1000));
    return weekNumber;
  }

  // Helper function to format date as YYYY-MM-DD in local timezone
  function formatDateLocal(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function getCurrentWeek(): WeekInfo {
    const now = new Date();
    console.log('🔍 [getCurrentDate] Current date:', now);
    console.log('🔍 [getCurrentDate] Current date:', formatDateLocal(now));
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
    const startDateStr = formatDateLocal(startOfWeek);
    const endDateStr = formatDateLocal(endOfWeek);
    console.log('🔍 [getCurrentWeek] Start of week:', startDateStr);
    console.log('🔍 [getCurrentWeek] End of week:', endDateStr);
    return {
      startDate: startDateStr,
      endDate: endDateStr,
      weekNumber: getWeekNumberFromDate(startDateStr)
    };
  }

  function getWeekRange(week: WeekInfo): string {
    // Parse date string YYYY-MM-DD directly without timezone conversion
    const parseDateString = (dateStr: string): string => {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    };
    const start = parseDateString(week.startDate);
    const end = parseDateString(week.endDate);
    return `${start} - ${end}`;
  }

  function formatDateForAPI(date: Date): string {
    // Convert Date to YYYY-MM-DD format for API using local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Helper function to add days to a date string (YYYY-MM-DD) without timezone issues
  function addDaysToDateString(dateString: string, days: number): string {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day + days);
    return formatDateForAPI(date);
  }

  function parseDateFromAPI(dateString: string): string {
    // Server now returns date as YYYY-MM-DD string, so just return it directly
    if (typeof dateString === 'string') {
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      // If it contains timestamp, extract just the date part (YYYY-MM-DD)
      if (dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      // If it's in other format, try to extract YYYY-MM-DD
      const dateMatch = dateString.match(/(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        return dateMatch[1];
      }
      return dateString;
    }
    // If it's not a string, return as is (shouldn't happen)
    return String(dateString);
  }

  // Sync with URL params
  useEffect(() => {
    if (startDate && endDate) {
      const newWeek = {
        startDate,
        endDate,
        weekNumber: getWeekNumberFromDate(startDate)
      };
      setCurrentWeek(newWeek);
    }
  }, [startDate, endDate]);

  // Load data
  useEffect(() => {
    let isMounted = true;
    
    const loadDataSafe = async () => {
      if (!isMounted) return;
      await loadData();
    };
    
    loadDataSafe();
    
    return () => {
      isMounted = false;
    };
  }, [currentWeek, user]);

  const loadHolidays = async () => {
    try {
      const response = await fetch('/api/holidays');
      const data = await response.json();
      if (data.success) {
        setHolidays(data.data);
      }
    } catch (err) {
      console.error('Error loading holidays:', err);
    }
  };



  const loadData = async () => {
    if (isLoadingRef.current) {
      return;
    }
    
    try {
      isLoadingRef.current = true;
      setLoading(true, 'Đang tải dữ liệu...');
      clearError();
      
      // Load staff
      const staffResponse = await fetch('/api/staff?all=true');
      const staffData = await staffResponse.json(); 
      
      if (staffData.success) {
        setStaff(staffData.data);
        
        // Auto-select logged-in user's staff_id if available (priority over manual selection)
        if (user?.id) {
          const loggedInStaff = staffData.data.find((s: Staff) => s.id === user.id && s.status === 'Hoạt động');
          if (loggedInStaff) {
            setSelectedStaffId(user.id);
            setStaffSearch(loggedInStaff.name);
          }
        }
      } else {
        throw new Error(staffData.error || 'Lỗi khi tải danh sách nhân viên');
      }
      
      // Load schedules
      const scheduleResponse = await fetch(`/api/schedule/${currentWeek.startDate}/${currentWeek.endDate}`);
      const scheduleData = await scheduleResponse.json();
      
      if (scheduleData.success) {
        const mappedSchedules = scheduleData.data.filter((s: any) => s.is_deleted === false).map((s: any) => {    
          return {
            id: s.id,
            staffId: s.staff_id,
            workDate: parseDateFromAPI(s.work_date),
            shift: s.shift,
            staffName: s.staff_name,
            staffRole: s.staff_role,
            staffColor: '#8d2a2c',
            staffTextColor: '#ffffff'
          };
        });
        setSchedules(mappedSchedules);
      } else {
        throw new Error(scheduleData.error || 'Lỗi khi tải lịch làm việc');
      }
      
      // Load holidays
      await loadHolidays();

      // Load registration lock status
      try {
        const token = localStorage.getItem('auth_token');
        const lockResponse = await fetch(`/api/schedule/registration-lock?startDate=${currentWeek.startDate}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const lockData = await lockResponse.json();
        if (lockData.success) {
          setLockInfo(lockData.data);
        }
      } catch (lockErr) {
        console.error('Error loading registration lock status:', lockErr);
      }
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Lỗi khi tải dữ liệu');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  const handleWeekChange = (direction: 'prev' | 'next') => {
    // Add/subtract 7 days from current week start date
    const daysToAdd = direction === 'next' ? 7 : -7;
    const startDate = addDaysToDateString(currentWeek.startDate, daysToAdd);
    const endDate = addDaysToDateString(startDate, 6);
    
    navigate(`/register/${startDate}/${endDate}`);
  };

  const handleStaffChange = (staffId: string) => {
    setSelectedStaffId(staffId);
    setHasChanges(false);
    setPendingChanges({ toAdd: [], toDelete: [] });
    setStaffSearch('');
    setShowStaffDropdown(false);
  };

  // Filter staff based on search (exclude SALE)
  const filteredStaffForSearch = staff.filter(s => {
    if (s.status !== 'Hoạt động') return false;
    if (s.name === 'SALE') return false; // Hide SALE staff
    if (!staffSearch.trim()) return true;
    const searchLower = staffSearch.toLowerCase();
    return s.name.toLowerCase().includes(searchLower) || 
           (s.role && s.role.toLowerCase().includes(searchLower));
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (staffSearchRef.current && !staffSearchRef.current.contains(event.target as Node)) {
        setShowStaffDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const removeSchedule = (scheduleId: string) => {
    // Check if it's a temporary schedule (pending addition)
    if (scheduleId.startsWith('temp_')) {
      const schedule = schedules.find(s => s.id === scheduleId);
      if (schedule) {
        setPendingChanges(prev => ({
          ...prev,
          toAdd: prev.toAdd.filter(p => 
            !(p.staffId === schedule.staffId && p.workDate === schedule.workDate && p.shift === schedule.shift)
          )
        }));
      }
    } else {
      // Add to pending deletions (if not already pending delete)
      setPendingChanges(prev => ({
        ...prev,
        toDelete: prev.toDelete.includes(scheduleId) ? prev.toDelete : [...prev.toDelete, scheduleId]
      }));
    }

    // Update local state for immediate UI feedback
    setSchedules(prev => prev.filter(s => s.id !== scheduleId));
    setHasChanges(true);
  };

  const handleShiftClick = (workDate: string, shift: string) => {
    if (!selectedStaffId) {
      setError('Vui lòng chọn nhân viên trước');
      return;
    }

    if (!isUserAdmin && lockInfo && !lockInfo.canRegister) {
      setError('Cổng đăng ký ca cho tuần này đã đóng (quá hạn 00:00 Chủ Nhật). Vui lòng liên hệ Quản lý để được hỗ trợ.');
      return;
    }

    // Check if already has schedule for this slot by selected staff
    const existingSchedule = schedules.find(s => 
      s.staffId === selectedStaffId && s.workDate === workDate && s.shift === shift
    );

    if (existingSchedule) {
      if (isUserAdmin) {
        // Quản trị viên được quyền xóa / sửa ca đã đăng ký
        removeSchedule(existingSchedule.id);
        return;
      }
      // Not allowed to remove existing schedule for regular staff
      setError('Không thể xóa ca đã đăng ký. Vui lòng liên hệ quản lý để hỗ trợ.');
      return;
    } else {
      // Add new schedule for selected staff
      addScheduleToPending(selectedStaffId, workDate, shift);
    }
  };

  const addScheduleToPending = (staffId: string, workDate: string, shift: string) => {
    // Check for duplicate in pending additions
    const pendingDuplicate = pendingChanges.toAdd.find(p => 
      p.staffId === staffId && p.workDate === workDate && p.shift === shift
    );
    
    if (pendingDuplicate) {
      setError('Ca làm việc này đã được thêm vào danh sách chờ');
      return;
    }

    // Add to pending changes
    setPendingChanges(prev => ({
      ...prev,
      toAdd: [...prev.toAdd, { staffId, workDate, shift }]
    }));

    // Update local state for immediate UI feedback
    const staffMember = staff.find(s => s.id === staffId);
    if (staffMember) {
      const tempId = `temp_${Date.now()}_${Math.random()}`;
      const newSchedule: Schedule = {
        id: tempId,
        staffId,
        workDate,
        shift,
        staffName: staffMember.name,
        staffRole: staffMember.role,
        staffColor: '#28a745',
        staffTextColor: '#ffffff'
      };
      setSchedules(prev => [...prev, newSchedule]);
      setHasChanges(true);
    }
  };

  const savePendingChanges = async () => {
    if (pendingChanges.toAdd.length === 0 && pendingChanges.toDelete.length === 0) {
      setError('Không có thay đổi nào để lưu');
      return;
    }

    if (!isUserAdmin && lockInfo && !lockInfo.canRegister) {
      setError('Cổng đăng ký ca cho tuần này đã bị khóa. Vui lòng liên hệ Quản lý.');
      return;
    }

    try {
      setLoading(true, 'Đang lưu thay đổi...');
      
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/schedule/batch', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(pendingChanges)
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Clear pending changes
        setPendingChanges({ toAdd: [], toDelete: [] });
        setHasChanges(false);
        
        // Reload data to get updated schedules from server
        await loadData();
        
        const addedCount = data.data?.added?.length || 0;
        const deletedCount = data.data?.deleted || 0;
        const msgParts: string[] = [];
        if (addedCount > 0) msgParts.push(`đã đăng ký ${addedCount} ca mới`);
        if (deletedCount > 0) msgParts.push(`đã xóa ${deletedCount} ca`);
        showSuccess(msgParts.length > 0 ? `Cập nhật thành công: ${msgParts.join(', ')}` : 'Đã lưu thay đổi thành công');
      } else {
        setError(data.error || 'Lỗi khi lưu thay đổi');
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      setError('Lỗi khi lưu thay đổi');
    } finally {
      setLoading(false);
    }
  };

  const formatDeadlineText = (deadlineStr?: string) => {
    if (!deadlineStr) return '';
    try {
      const d = new Date(deadlineStr);
      const dateStr = new Intl.DateTimeFormat('vi-VN', {
        timeZone: 'Asia/Ho_Chi_Minh',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(d);
      return `00:00 Chủ Nhật, ngày ${dateStr}`;
    } catch {
      return deadlineStr;
    }
  };

  const handleToggleLock = async (unlock: boolean) => {
    try {
      setIsTogglingLock(true);
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/schedule/registration-lock/toggle', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          weekStartDate: currentWeek.startDate,
          isUnlocked: unlock,
          note: unlock ? `Admin ${user?.name || ''} thả khóa đăng ký` : `Admin ${user?.name || ''} khóa cổng đăng ký`
        })
      });
      const data = await res.json();
      if (data.success) {
        setLockInfo(data.data);
        showSuccess(unlock ? 'Đã thả khóa! Nhân viên hiện có thể đăng ký ca cho tuần này.' : 'Đã khóa lại cổng đăng ký ca tuần này.');
      } else {
        setError(data.error || 'Không thể thay đổi trạng thái khóa ca');
      }
    } catch (e: any) {
      setError(e.message || 'Lỗi kết nối khi thay đổi khóa ca');
    } finally {
      setIsTogglingLock(false);
    }
  };

  const handleTriggerReminder = async () => {
    if (!window.confirm('Hệ thống sẽ gửi email cảnh báo đăng ký ca đến tất cả nhân viên Part-time còn hoạt động chưa đăng ký ca tuần tới, đồng thời gửi báo cáo tới vuhoan0095@gmail.com và ngochuynhak99ngochuynh@gmail.com.\n\nBạn có muốn gửi ngay bây giờ không?')) {
      return;
    }
    try {
      setIsTriggeringReminder(true);
      setLoading(true, 'Đang gửi email nhắc nhở ca...');
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/schedule/trigger-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const data = await res.json();
      if (data.success) {
        showSuccess(`✅ Đã gửi thông báo thành công!\n- NV Part-time chưa đăng ký: ${data.data.unregisteredCount}/${data.data.totalParttime}\n- Email NV nhận: ${data.data.staffEmailsSent.length}\n- Báo cáo gửi Admin: ${data.data.adminEmailsSent.join(', ')}`);
      } else {
        setError(data.error || 'Không thể gửi email nhắc nhở');
      }
    } catch (e: any) {
      setError(e.message || 'Lỗi kết nối');
    } finally {
      setIsTriggeringReminder(false);
      setLoading(false);
    }
  };

  const cancelPendingChanges = () => {
    setPendingChanges({ toAdd: [], toDelete: [] });
    setHasChanges(false);
    // Reload data to reset to server state
    loadData();
  };

  const getScheduleForSlot = (workDate: string, shift: string) => {
    // Show all schedules for the slot, not just selected staff
    const result = schedules.filter(s => 
      s.workDate === workDate && 
      s.shift === shift
    );
    return result;
  };

  const showSuccess = (message: string) => {
    // You can implement a toast notification here
    alert(message); // Temporary solution
  };

  const goToCurrentWeek = () => {
    const currentWeek = getCurrentWeek();
    navigate(`/register/${currentWeek.startDate}/${currentWeek.endDate}`);
  };

  return (
    <div className="register-page" style={{ width: '100%', overflowX: 'auto' }}>
      <div className="container" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="header">
          <h1 style={{ fontSize: isMobile ? '18px' : '24px', marginBottom: isMobile ? '10px' : '15px' }}>
            <i className="fas fa-user-clock"></i> Đăng Ký Ca Làm Việc
          </h1>
          
          <div style={{ marginBottom: '15px', display: isMobile ? 'none' : 'block' }}>
            <button
              onClick={() => navigate('/checkinout')}
              style={{
                padding: '10px 20px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              <i className="fas fa-clock"></i> Điểm danh
            </button>
          </div>
          
          <div className="week-navigation" style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: isMobile ? '8px' : '15px',
            flexWrap: 'nowrap',
            width: '100%'
          }}>
            <button 
              className="nav-btn" 
              onClick={() => handleWeekChange('prev')}
              style={{
                padding: isMobile ? '8px' : '10px 15px',
                fontSize: isMobile ? '14px' : '14px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                width: isMobile ? '32px' : 'auto',
                height: isMobile ? '32px' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: isMobile ? '4px' : '4px',
                border: isMobile ? '1px solid #ddd' : 'none',
                background: isMobile ? 'white' : 'transparent',
                color: isMobile ? '#333' : 'inherit'
              }}
            >
              <i className="fas fa-chevron-left"></i>
              {!isMobile && <span>Tuần trước</span>}
            </button>
            <div className="current-week" style={{
              fontSize: isMobile ? '11px' : '14px',
              textAlign: 'center',
              flex: '1',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {getWeekRange(currentWeek)}
              {hasChanges && (
                <span className="changes-indicator" style={{ 
                  display: 'block', 
                  marginTop: '5px',
                  fontSize: isMobile ? '10px' : '12px'
                }}>
                  <i className="fas fa-edit"></i> Có thay đổi chưa lưu
                </span>
              )}
            </div>
            <button 
              className="nav-btn" 
              onClick={() => handleWeekChange('next')}
              style={{
                padding: isMobile ? '8px' : '10px 15px',
                fontSize: isMobile ? '14px' : '14px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                width: isMobile ? '32px' : 'auto',
                height: isMobile ? '32px' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: isMobile ? '4px' : '4px',
                border: isMobile ? '1px solid #ddd' : 'none',
                background: isMobile ? 'white' : 'transparent',
                color: isMobile ? '#333' : 'inherit'
              }}
            >
              {!isMobile && <span>Tuần sau</span>}
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        {/* Banner cảnh báo khóa đăng ký ca */}
        {lockInfo && lockInfo.isLocked && (
          <div style={{
            margin: '12px 0 16px 0',
            padding: '14px 18px',
            backgroundColor: '#fff1f2',
            border: '1px solid #fecdd3',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            color: '#9f1239',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#ffe4e6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: '18px',
              color: '#e11d48'
            }}>
              <i className="fas fa-lock"></i>
            </div>
            <div style={{ flex: 1, fontSize: isMobile ? '12px' : '13px', lineHeight: '1.5' }}>
              <div style={{ fontWeight: '700', fontSize: isMobile ? '13px' : '15px', marginBottom: '3px', color: '#881337' }}>
                Cổng đăng ký ca cho tuần này đã đóng!
              </div>
              <div>
                Hạn chót đăng ký tuần {getWeekRange(currentWeek)} là <strong>{formatDeadlineText(lockInfo.deadline)}</strong>. Cổng đăng ký đã tự động khóa sau 00:00 Chủ Nhật. {isUserAdmin ? 'Bạn đang đăng nhập quyền Quản trị viên nên vẫn có thể đăng ký, chỉnh sửa hoặc xóa ca.' : 'Nhân viên thường không thể tự đăng ký trừ khi Quản lý thả khóa.'}
              </div>
            </div>
          </div>
        )}

        {lockInfo && !lockInfo.isLocked && lockInfo.isUnlockedByAdmin && (
          <div style={{
            margin: '12px 0 16px 0',
            padding: '14px 18px',
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            color: '#166534',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: '18px',
              color: '#16a34a'
            }}>
              <i className="fas fa-unlock-alt"></i>
            </div>
            <div style={{ flex: 1, fontSize: isMobile ? '12px' : '13px', lineHeight: '1.5' }}>
              <div style={{ fontWeight: '700', fontSize: isMobile ? '13px' : '15px', marginBottom: '3px', color: '#14532d' }}>
                Quản lý đã thả khóa mở cổng đăng ký ca!
              </div>
              <div>
                Cổng đăng ký tuần {getWeekRange(currentWeek)} đang được mở khóa{lockInfo.unlockedBy ? ` bởi Quản lý ${lockInfo.unlockedBy}` : ''}. Nhân viên có thể tiếp tục chọn ca và lưu thay đổi.
              </div>
            </div>
          </div>
        )}

        {lockInfo && !lockInfo.isLocked && !lockInfo.isPastDeadline && (
          <div style={{
            margin: '8px 0 14px 0',
            padding: '10px 14px',
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: '#1e40af',
            fontSize: isMobile ? '11px' : '12px'
          }}>
            <i className="fas fa-clock" style={{ color: '#2563eb', fontSize: '15px' }}></i>
            <span>
              Hạn chót đăng ký ca tuần này: <strong>{formatDeadlineText(lockInfo.deadline)}</strong> (00:00 Chủ Nhật). Hệ thống tự động gửi email nhắc nhở lúc 16:00 Thứ Bảy.
            </span>
          </div>
        )}

        {/* Thanh công cụ quản lý dành cho Admin */}
        {isUserAdmin && (
          <div style={{
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: isMobile ? '12px' : '14px 18px',
            marginBottom: '16px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{
                  backgroundColor: '#0f172a',
                  color: '#fff',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '700',
                  letterSpacing: '0.5px'
                }}>
                  <i className="fas fa-shield-alt"></i> QUẢN LÝ
                </span>
                <span style={{ fontSize: '13px', color: '#334155', fontWeight: '600' }}>
                  Trạng thái cổng tuần {getWeekRange(currentWeek)}:
                </span>
                {lockInfo?.isLocked ? (
                  <span style={{
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    padding: '3px 9px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    <i className="fas fa-lock"></i> Đang Khóa
                  </span>
                ) : lockInfo?.isUnlockedByAdmin ? (
                  <span style={{
                    backgroundColor: '#dcfce7',
                    color: '#16a34a',
                    padding: '3px 9px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    <i className="fas fa-unlock"></i> Đã Thả Khóa
                  </span>
                ) : (
                  <span style={{
                    backgroundColor: '#e0f2fe',
                    color: '#0284c7',
                    padding: '3px 9px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    <i className="fas fa-check-circle"></i> Trong Hạn Đăng Ký
                  </span>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '8px',
                width: isMobile ? '100%' : 'auto',
                flexWrap: 'wrap'
              }}>
                {/* Nút Thả khóa / Khóa lại */}
                {lockInfo?.isLocked ? (
                  <button
                    onClick={() => handleToggleLock(true)}
                    disabled={isTogglingLock}
                    style={{
                      backgroundColor: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 14px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: isTogglingLock ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flex: isMobile ? '1' : 'auto',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="fas fa-unlock"></i>
                    {isTogglingLock ? 'Đang mở khóa...' : 'Thả khóa đăng ký ca'}
                  </button>
                ) : (
                  <button
                    onClick={() => handleToggleLock(false)}
                    disabled={isTogglingLock}
                    style={{
                      backgroundColor: '#e11d48',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 14px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: isTogglingLock ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flex: isMobile ? '1' : 'auto',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="fas fa-lock"></i>
                    {isTogglingLock ? 'Đang khóa...' : 'Khóa cổng đăng ký'}
                  </button>
                )}

                {/* Nút gửi email nhắc nhở Part-time */}
                <button
                  onClick={handleTriggerReminder}
                  disabled={isTriggeringReminder}
                  style={{
                    backgroundColor: '#4f46e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: isTriggeringReminder ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flex: isMobile ? '1' : 'auto',
                    justifyContent: 'center'
                  }}
                  title="Gửi email nhắc nhở cho tất cả NV Part-time chưa đăng ký ca tuần tới và gửi báo cáo cho Admin"
                >
                  <i className="fas fa-paper-plane"></i>
                  {isTriggeringReminder ? 'Đang gửi...' : 'Gửi mail nhắc ca Part-time'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="control-panel" style={{ 
          marginBottom: isMobile ? '15px' : '20px',
          width: '100%'
        }}>
          <div className="staff-selector" style={{ width: '100%' }}>
            <h3 style={{ fontSize: isMobile ? '16px' : '18px', marginBottom: '10px' }}>
              <i className="fas fa-user-tag"></i> Chọn Nhân Viên
            </h3>
            <div 
              ref={staffSearchRef}
              style={{ position: 'relative', width: '100%' }}
            >
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="staff-dropdown"
                  placeholder="Tìm kiếm nhân viên (tên hoặc vai trò)..."
                  value={staffSearch}
                  onChange={(e) => {
                    setStaffSearch(e.target.value);
                    setShowStaffDropdown(true);
                  }}
                  onFocus={() => setShowStaffDropdown(true)}
                  style={{
                    width: '100%',
                    padding: '10px 40px 10px 15px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <i 
                  className="fas fa-search" 
                  style={{
                    position: 'absolute',
                    right: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#999',
                    pointerEvents: 'none'
                  }}
                />
              </div>
              
              {showStaffDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #ddd',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                >
                  {filteredStaffForSearch.length > 0 ? (
                    filteredStaffForSearch.map(s => (
                      <div
                        key={s.id}
                        onClick={() => handleStaffChange(s.id)}
                        style={{
                          padding: '12px 15px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: selectedStaffId === s.id ? '#e3f2fd' : 'white',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedStaffId !== s.id) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedStaffId !== s.id) {
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <div style={{ fontWeight: '500', color: '#333' }}>
                          {s.name}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '15px', textAlign: 'center', color: '#999' }}>
                      Không tìm thấy nhân viên
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {selectedStaffId && (
              <div className="selected-staff-info show" style={{ marginTop: '15px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <div className="staff-name-display">
                    {staff.find(s => s.id === selectedStaffId)?.name}
                  </div>
                  <button
                    onClick={() => {
                      setSelectedStaffId('');
                      setStaffSearch('');
                      setHasChanges(false);
                      setPendingChanges({ toAdd: [], toDelete: [] });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#666',
                      cursor: 'pointer',
                      padding: '5px 10px',
                      fontSize: '14px'
                    }}
                    title="Bỏ chọn"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="instruction-text">
                  <i className="fas fa-hand-pointer"></i> Nhấp vào các ô trong bảng để đăng ký ca làm việc
                </div>
                <div className="schedule-count">
                  <i className="fas fa-calendar-check"></i>
                  Đã đăng ký: {schedules.filter(s => s.staffId === selectedStaffId).length} ca trong tuần này
                </div>
              </div>
            )}

            <div className="save-controls" style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '10px' : '10px',
              marginTop: '15px',
              flexWrap: 'wrap'
            }}>
              <button 
                className={`save-btn ${hasChanges && (isUserAdmin || !lockInfo || lockInfo.canRegister) ? 'has-changes' : ''}`} 
                disabled={!hasChanges || (!isUserAdmin && Boolean(lockInfo && !lockInfo.canRegister))}
                onClick={savePendingChanges}
                title={!isUserAdmin && lockInfo && !lockInfo.canRegister ? 'Cổng đăng ký ca tuần này đã bị đóng' : ''}
                style={{
                  flex: isMobile ? '1 1 100%' : '1',
                  padding: isMobile ? '10px' : '12px',
                  fontSize: isMobile ? '13px' : '14px',
                  cursor: !isUserAdmin && lockInfo && !lockInfo.canRegister ? 'not-allowed' : 'pointer',
                  opacity: !isUserAdmin && lockInfo && !lockInfo.canRegister ? 0.6 : 1
                }}
              >
                <i className="fas fa-save"></i>
                {isMobile ? 'Lưu' : 'Lưu thay đổi'}
                {hasChanges && (
                  <span className="change-counter">
                    {pendingChanges.toAdd.length + pendingChanges.toDelete.length}
                  </span>
                )}
              </button>
              <button 
                className={`cancel-btn ${hasChanges ? 'has-changes' : ''}`} 
                disabled={!hasChanges}
                onClick={cancelPendingChanges}
                style={{
                  flex: isMobile ? '1 1 100%' : '1',
                  padding: isMobile ? '10px' : '12px',
                  fontSize: isMobile ? '13px' : '14px'
                }}
              >
                <i className="fas fa-undo"></i>
                {isMobile ? 'Hủy' : 'Hủy thay đổi'}
              </button>
              <button 
                className="current-week-btn"
                onClick={goToCurrentWeek}
                style={{
                  flex: isMobile ? '1 1 100%' : '1',
                  padding: isMobile ? '10px' : '12px',
                  fontSize: isMobile ? '13px' : '14px'
                }}
              >
                <i className="fas fa-calendar-day"></i>
                {isMobile ? 'Tuần này' : 'Tuần hiện tại'}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ margin: '20px', padding: '12px', background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}

        {!selectedStaffId && (
          <div className="instruction-banner">
            <i className="fas fa-info-circle"></i>
            <span>
              Vui lòng chọn nhân viên để đăng ký ca làm việc. Bạn có thể xem tất cả ca đã đăng ký trong tuần này.
              {isUserAdmin ? ' Bạn đang thao tác với quyền Quản trị viên: có thể đăng ký, sửa hoặc xóa ca.' : ' Lưu ý: Không thể xóa ca đã đăng ký.'}
            </span>
          </div>
        )}

        <div className="schedule-grid" style={{ 
          overflowX: 'auto',
          width: '100%',
          WebkitOverflowScrolling: 'touch'
        }}>
          <table className="schedule-table" style={{
            minWidth: isMobile ? '600px' : '100%',
            fontSize: isMobile ? '12px' : '14px'
          }}>
            <thead>
              <tr>
                <th style={{ width: isMobile ? '60px' : '80px', fontSize: isMobile ? '11px' : '14px' }}>Ca</th>
                {DAYS.map((day, dayIndex) => {
                  // Parse date string YYYY-MM-DD directly and add days
                  const [year, month, dayOfMonth] = currentWeek.startDate.split('-').map(Number);
                  const date = new Date(year, month - 1, dayOfMonth + dayIndex);
                  const dayNumber = date.getDate();
                  const monthNumber = date.getMonth() + 1;
                  const workDateStr = addDaysToDateString(currentWeek.startDate, dayIndex);
                  const holiday = holidays.find(h => h.holiday_date === workDateStr);
                  
                  return (
                    <th key={day} style={{ fontSize: isMobile ? '11px' : '14px', padding: isMobile ? '8px 4px' : '10px', position: 'relative' }}>
                      <div>{isMobile ? day.replace('Thứ ', 'T.') : day}</div>
                      <div style={{ fontSize: isMobile ? '10px' : '12px', marginTop: '4px', fontWeight: 'normal' }}>
                        {dayNumber}/{monthNumber}
                      </div>
                      {holiday && (
                        <div style={{ marginTop: '4px' }}>
                          <span style={{
                            background: '#ffc107',
                            color: '#1e293b',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: '700',
                            display: 'inline-block'
                          }}>
                            X{holiday.multiplier}
                          </span>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {SHIFTS.map(shift => (
                <tr key={shift}>
                  <td className="shift-header">{shift}</td>
                  {DAYS.map((day, dayIndex) => {
                    // Add days to date string directly without timezone conversion
                    const workDateStr = addDaysToDateString(currentWeek.startDate, dayIndex);
                    const shiftSchedules = getScheduleForSlot(workDateStr, shift);
                    // Check if this column has any schedule with staff name "SALE"
                    const hasSaleSchedule = schedules.some(s => 
                      s.workDate === workDateStr && s.staffName === 'SALE'
                    );
                    
                    const isSlotDisabled = !isUserAdmin && Boolean(lockInfo && !lockInfo.canRegister);
                    
                    return (
                      <td 
                        key={`${day}-${shift}`}
                        className={`shift-cell ${selectedStaffId ? 'selected-staff-ready' : 'no-staff-selected'} ${isSlotDisabled ? 'slot-locked' : ''}`}
                        style={{
                          backgroundColor: hasSaleSchedule ? '#ffc0cb' : (isSlotDisabled ? '#f9fafb' : 'transparent'),
                          cursor: isSlotDisabled ? 'not-allowed' : (selectedStaffId ? 'pointer' : 'default'),
                          opacity: isSlotDisabled ? 0.85 : 1
                        }}
                        onClick={() => {
                          if (isSlotDisabled) {
                            setError('Cổng đăng ký ca cho tuần này đã đóng (quá hạn 00:00 Chủ Nhật). Vui lòng liên hệ Quản lý.');
                            return;
                          }
                          if (selectedStaffId) {
                            const existingSchedule = schedules.find(s => 
                              s.staffId === selectedStaffId && s.workDate === workDateStr && s.shift === shift
                            );
                            if (existingSchedule && !isUserAdmin) {
                              return;
                            }
                            handleShiftClick(workDateStr, shift);
                          }
                        }}
                        title={isSlotDisabled ? 'Cổng đăng ký ca cho tuần này đã đóng (sau 00:00 Chủ Nhật)' : (isUserAdmin ? 'Nhấp để đăng ký / hủy ca' : '')}
                      >
                        {shiftSchedules.filter(s => s.staffName !== 'SALE').map(schedule => {
                          const isSelectedStaff = selectedStaffId === schedule.staffId;
                          const isNewAssignment = schedule.id.startsWith('temp_');
                          
                          return (
                            <div 
                              key={schedule.id}
                              className={`staff-assignment ${
                                isNewAssignment ? 'new-assignment' : ''
                              } ${isSelectedStaff ? 'selected-staff-schedule' : ''}`}
                              style={{
                                backgroundColor: isSelectedStaff ? '#28a745' : schedule.staffColor,
                                color: isSelectedStaff ? '#ffffff' : schedule.staffTextColor,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '4px'
                              }}
                            >
                              <span>{schedule.staffName}</span>
                              {isUserAdmin && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeSchedule(schedule.id);
                                  }}
                                  style={{
                                    background: 'rgba(0, 0, 0, 0.2)',
                                    border: 'none',
                                    color: '#ffffff',
                                    cursor: 'pointer',
                                    borderRadius: '50%',
                                    width: '15px',
                                    height: '15px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    lineHeight: '1',
                                    padding: 0,
                                    marginLeft: '2px',
                                    flexShrink: 0
                                  }}
                                  title={`Xóa ca của ${schedule.staffName} (Admin)`}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

